import { Checkbox, Dialog, PopoverPosition, Tooltip } from "construct-ui";
import moment from "moment";
import ToggleButtonGroupComponent, { BUTTON_TYPE } from "../../components/common/toggle-button-group.component";
import DatePickerComponent from "../../components/date-picker/date-picker.component";
import QuarterPickerComponent from "../../components/date-picker/quarter-picker.component";
import YearPickerComponent from "../../components/date-picker/year-picker.component";
import SortableMultiSelectComponent from "../../components/multi-select/sortable-multi-select.component";
import PivotTableComponent from "../../components/pivot-table/pivot-table.component";
import DynamicReportFilterComponent from "../../components/report/dynamic-report-filter.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import { DATE_TYPE_CODES, formats as FORMATS, KEYS_SEPARATOR, KEY_VALUE_SEPARATOR } from "../../config/constants";
import loginModel from "../../models/login.model";
import ReportModel from "../../models/report/report-model.model";
import NonNullableObjectProxy from "../../utils/NonNullableObjectProxy";
import ViewBase from "../view-base.view";
import { stringifyFilterParams } from './utils.js';

export default function CuztomizedReportView() {
  ViewBase.call(this);

  let dataGroupOptions = [];
  let dataSourceCode;
  let filterParams;
  let isLoadedFromTemplate = false;
  let isOpenLoadReportTemplateDialog = false;
  let isOpenSaveReportTemplateDialog = false;
  let lastGenerated;
  let loadedTemplateDetails;
  let loadTemplateParams;
  let periodTypes = [];
  let saveTemplateParams;
  let reportFilterSection;
  let templateOptions = [];
  let infoSettings = [];
  let xAxisOptions;
  let yAxisOptions;
  let zAxisOptions;
  let customCellFormat;
  // let isCompareDates = false;    // Determines if we're comparing dates
  let isRangeTimeLimitExceed = false; // Determines the limit range date to generate report
  let isCompareTimeLimitExceed = false; // Determines the limit compare date to generate report

  let isMsa = false;

  // Cached data
  const cachedTemplateDataGroup = new Map();
  const cachedTemplates = new Map();
  const cachedReportInfo = new Map();

  const SELECT_TEMPLATE_FORM_NAME = 'select-template-form';
  const SAVE_TEMPLATE_FORM_NAME = 'save-template-form';

  const INFO_SETTINGS = {
    MAX_PERIOD: 'MaxPeriod',
    MIN_PERIOD: 'MinPeriod',
    PERIOD_SELECTION_LIMIT: 'PeriodSelectionLimit',
    COMPARE_DATE: 'CompareDate'
  }

  const DEFAULT_PERIOD_ORDER = [
    DATE_TYPE_CODES.MONTH,
    DATE_TYPE_CODES.WEEK,
  ];

  const TEMPLATE_TYPE = {
    PERSONAL: 'Personal',
    PUBLIC: 'Public',
  }

  const DIFF_MEASUREMENTS = {
    D: { unit: 'days', format: FORMATS.apiDateFormat },
    W: { unit: 'weeks', format: FORMATS.API_WEEK_FORMAT },
    M: { unit: 'months', format: FORMATS.API_MONTH_FORMAT },
    Y: { unit: 'years', format: FORMATS.YEAR_FORMAT },
  }

  const DEFAULT_TEMPLATE_PARAMS = Object.freeze({
    period: null,
    type: TEMPLATE_TYPE.PERSONAL,
    group: null,
    template: null,
  });

  const DEFAULT_FILTER_PARAMS = Object.freeze({
    period: null,           // Report period type
    mtdDay: '1',            // Day for MTD report (only applicable for MTD report)
    mtdCompareDay: '1',
    fromDate: moment().format(FORMATS.ISO_DATE_FORMAT),   // Period start date
    toDate: moment().format(FORMATS.ISO_DATE_FORMAT),     // Period end date
    parameters: null,       // Filters selected from `DynamicReportFilterComponent`
    xAxis: [],              // Column headers
    yAxis: [],              // Row headers
    zAxis: [],              // Data display
    xAxisOrderedCodes: [],  // Order of column header codes
    yAxisOrderedCodes: [],  // Order of row header codes
    zAxisOrderedCodes: [],  // Order of data display codes
    isCompareDates: false,    // Determines if we're comparing dates
    fromCompareDate: null, // From compare start date
    toCompareDate: null    // To compare start date
  });

  const isDayPeriod = () => {
    return filterParams.period === DATE_TYPE_CODES.DAY;
  }

  const isWeekPeriod = () => {
    return filterParams.period === DATE_TYPE_CODES.WEEK;
  }

  const isMonthPeriod = () => {
    return filterParams.period === DATE_TYPE_CODES.MONTH;
  }

  const isYearPeriod = () => {
    return filterParams.period === DATE_TYPE_CODES.YEAR;
  }

  const isQuarterPeriod = () => {
    return filterParams.period === DATE_TYPE_CODES.QUARTER;
  }

  const isMTDPeriod = () => {
    return filterParams.period === DATE_TYPE_CODES.MTD;
  }

  /**
   * Array of day range: 1 ~ 31
   */
  const DAY_OPTIONS = (() => {
    const days = [];
    for (let x = 1; x <= 31; x++) {
      days.push({ code: x.toString(), name: x.toString() });
    }
    return days;
  })()

  const getDefaultPeriod = () => {
    const defaultPeriod = DEFAULT_PERIOD_ORDER.find(periodCode => {
      return periodTypes.some(period => period.code === periodCode);
    })
    return defaultPeriod ?? periodTypes[0]?.code;
  }

  const getDefaultDateByPeriod = (period) => {
    switch (period) {
      case DATE_TYPE_CODES.MONTH:
        return moment().format(FORMATS.API_MONTH_FORMAT);

      case DATE_TYPE_CODES.MTD:
        return moment().format(FORMATS.API_MONTH_FORMAT);

      case DATE_TYPE_CODES.WEEK:
        return moment().format(FORMATS.API_WEEK_FORMAT);

      case DATE_TYPE_CODES.DAY:
        return moment().format(FORMATS.apiDateFormat);

      case DATE_TYPE_CODES.QUARTER:
        return moment().format(FORMATS.API_QUARTER_FORMAT);

      case DATE_TYPE_CODES.YEAR:
        return moment().format(FORMATS.YEAR_FORMAT);

      default:
        return moment().format(FORMATS.apiDateFormat);
    }
  }

  const resetLoadTemplateParams = () => {
    loadTemplateParams = structuredClone(DEFAULT_TEMPLATE_PARAMS);
    loadTemplateParams['period'] = getDefaultPeriod();
  }

  const resetFilterParams = () => {
    let currentPeriodType = filterParams.period;
    filterParams = structuredClone(DEFAULT_FILTER_PARAMS);
    filterParams.period = currentPeriodType; // Retain `Period` value
    filterParams.fromDate = getDefaultDateByPeriod(currentPeriodType);
    filterParams.toDate = getDefaultDateByPeriod(currentPeriodType);

    loadedTemplateDetails = undefined;
    isRangeTimeLimitExceed = false;
    isCompareTimeLimitExceed = false;
  }

  const getPeriodTypes = () => {
    return ReportModel.getReportConfigReportPeriod(dataSourceCode)
      .then((result) => {
        periodTypes = result || [];
        // Set default value
        filterParams.period = getDefaultPeriod();
        filterParams.fromDate = getDefaultDateByPeriod(filterParams.period);
        filterParams.toDate = getDefaultDateByPeriod(filterParams.period);
        loadTemplateParams['period'] = getDefaultPeriod();

        return periodTypes;
      })
      .catch((error) => {
        periodTypes = [];
        this.handleError(error);
      })
  }

  const getReportInfo = () => {
    // Get cached report structure
    let cachedData = cachedReportInfo.get(`${dataSourceCode}-${filterParams.period}`);
    if (cachedData) {
      return Promise.resolve(cachedData);
    };
    // Get report structure list via API
    return ReportModel.getReportInfo(dataSourceCode, filterParams.period)
      .then((result) => {
        cachedReportInfo.set(`${dataSourceCode}-${filterParams.period}`, result);
        return result;
      })
  }

  const updateReportStructureOptions = () => {
    return getReportInfo()
      .then((result) => {
        xAxisOptions = result?.xAxis ?? [];
        yAxisOptions = result?.yAxis ?? [];
        zAxisOptions = result?.zAxis ?? [];
        customCellFormat = result?.customCellFormat ?? undefined;   // Custom format for Flexmonster cell
        infoSettings = result?.settings ?? [];
      });
  }

  const getSettingByCode = (code, settings = infoSettings) => {
    return settings.find(s => s.code === code)?.name;
  }

  const getPeriodSelectionLimit = () => {
    return Number(getSettingByCode(INFO_SETTINGS.PERIOD_SELECTION_LIMIT));
  }

  const getMaxPeriod = () => {
    if (getSettingByCode(INFO_SETTINGS.MAX_PERIOD) === undefined) {
      return 9999; // if API not return MaxPeriod, means no limitation
    } else {
      return Number(getSettingByCode(INFO_SETTINGS.MAX_PERIOD)) || 0;
    }
  }

  const getMinPeriod = () => {
    if (getSettingByCode(INFO_SETTINGS.MIN_PERIOD) === undefined) {
      return -9999; // if API not return MinPeriod, means no limitation
    } else {
      return Number(getSettingByCode(INFO_SETTINGS.MIN_PERIOD)) || 0;
    }
  }

  const setFilter = (prop, value) => {
    if (!prop || filterParams[prop] === value) return false;
    if (prop === "isCompareDates" && !value) {
      filterParams.toCompareDate = null;
      filterParams.fromCompareDate = null;
    }
    filterParams[prop] = value;
    return true;
  }

  const _getParams = () => {
    const params = {};
    const proxy = NonNullableObjectProxy(params);
    if (filterParams.isCompareDates) {
      if (!filterParams.xAxisOrderedCodes.includes('COMPARE_LABEL')) {
        filterParams.xAxisOrderedCodes.push('COMPARE_LABEL');
      }
    } else {
      filterParams.xAxisOrderedCodes = filterParams.xAxisOrderedCodes.filter(item => item !== 'COMPARE_LABEL')
    }
    proxy.countryCode = loginModel.credentials.country;
    proxy.dataSourceCode = dataSourceCode;
    proxy.periodCode = filterParams.period;
    proxy.fromDate = filterParams.fromDate + (isMTDPeriod() ? filterParams.mtdDay.padStart(2, '0') : '');
    proxy.toDate = filterParams.toDate + (isMTDPeriod() ? filterParams.mtdDay.padStart(2, '0') : '');
    proxy.parameters = stringifyFilterParams(filterParams.parameters);
    proxy.xAxis = filterParams.xAxisOrderedCodes;
    proxy.yAxis = filterParams.yAxisOrderedCodes;
    proxy.zAxis = filterParams.zAxisOrderedCodes;
    proxy.customCellFormat = customCellFormat;
    proxy.fromCompareDate = filterParams.isCompareDates ? filterParams.fromCompareDate + (isMTDPeriod() ? filterParams.mtdCompareDay.padStart(2, '0') : '') : null;
    proxy.toCompareDate = filterParams.isCompareDates ? filterParams.toCompareDate + (isMTDPeriod() ? filterParams.mtdCompareDay.padStart(2, '0') : '') : null;
    return params;
  }

  let generateReport = (e) => {
    // Form validation
    if (!this.validateForm(e, '#report-generate-form.needs-validation')) {
      return false;
    }

    // To generate report in `PivotTableComponent`
    lastGenerated = new Date().toISOString();

    // Scroll to pivot table
    let pivotTableEl = document.getElementById('customized-report-pivot');
    pivotTableEl.scrollIntoView()
  }

  let downloadReport = (e) => {
    // Form validation
    if (!this.validateForm(e, '#report-generate-form.needs-validation')) {
      return false;
    }

    this.showLoading();

    let _rawParam = _getParams();
    let _modelParam = {
      dataSourceCode: _rawParam.dataSourceCode, // "McsProProduct",
      periodCode: _rawParam.periodCode,         // "M",
      fromDate: _rawParam.fromDate,             // "202201",
      toDate: _rawParam.toDate,                 // "202203",
      fromCompareDate: _rawParam.fromCompareDate,             // "202201",
      toCompareDate: _rawParam.toCompareDate,                 // "202203",
      xAxis: _rawParam.xAxis?.join('^^') || '', // "Year^^Month",
      yAxis: _rawParam.yAxis?.join('^^') || '', // "BU^^Div",
      zAxis: _rawParam.zAxis?.join('^^') || '', // "SO Qty:=SELLOUT_QTY^^SO Amt:=SELLOUT_AMT"
      parameters: _rawParam.parameters,
    };
    let _filename = `${_rawParam.dataSourceCode}_Data_${_rawParam.fromDate}_${_rawParam.toDate}`;
    return ReportModel.downloadReportData(_modelParam, _filename, isMsa ? 'V2' : '')
      .catch(error => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  const showFilterAndScrollTop = () => {
    reportFilterSection = reportFilterSection || document.getElementById("report-filter-section");
    reportFilterSection.classList.add('show');    // Toggle to show filter
    document.querySelector('html').scrollTop = 0; // Scroll to top
  }

  const clearFilters = (e) => {
    e?.preventDefault();
    let form = document.querySelector('#report-generate-form.needs-validation');
    form.classList.remove('was-validated');
    resetFilterParams();
  }

  const saveTemplate = (e) => {
    // Form validation
    if (!this.validateForm(e, '#report-generate-form.needs-validation')) {
      return false;
    }

    // Open dialog
    openSaveReportTemplateDialog()
  }

  const submitSaveTemplateForm = (e) => {
    // Form validation
    if (!this.validateForm(e, `#${SAVE_TEMPLATE_FORM_NAME}.needs-validation`)) {
      return false;
    }

    this.showLoading();

    const payload = {
      dataSourceCode,
      type: saveTemplateParams.type,
      name: saveTemplateParams.template.name,
      group: saveTemplateParams.group?.code || '',
      period: filterParams.period,
      fromDate: filterParams.fromDate + (isMTDPeriod() ? filterParams.mtdDay.padStart(2, '0') : ''),
      toDate: filterParams.toDate + (isMTDPeriod() ? filterParams.mtdDay.padStart(2, '0') : ''),
      parameters: stringifyFilterParams(filterParams.parameters),
      xAxis: filterParams.xAxisOrderedCodes.join(KEYS_SEPARATOR),
      yAxis: filterParams.yAxisOrderedCodes.join(KEYS_SEPARATOR),
      zAxis: filterParams.zAxisOrderedCodes.join(KEYS_SEPARATOR),
    }
    if (filterParams.fromCompareDate) {
      payload.fromCompareDate = filterParams.fromCompareDate + (isMTDPeriod() ? filterParams.mtdCompareDay.padStart(2, '0') : '');
    }
    if (filterParams.toCompareDate) {
      payload.toCompareDate = filterParams.toCompareDate + (isMTDPeriod() ? filterParams.mtdCompareDay.padStart(2, '0') : '');
    }
    ReportModel.saveReportTemplate(payload)
      .then((result) => {
        cachedTemplates.clear();
        this.showMessage(result.message, saveTemplateParams.template.name, closeSaveReportTemplateDialog);
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  /**
   * @returns `true` if `dataSourceCode` has changed. Otherwise, `false`
   */
  const getDataSourceCode = () => {
    let hasChanged = (dataSourceCode !== m.route.param().dataSourceCode);

    if (hasChanged) {
      dataSourceCode = m.route.param().dataSourceCode;
    }

    return hasChanged;
  }

  const handleTemplateFieldChange = (key, value) => {
    // Reset Group field & Template field
    loadTemplateParams.group = null;
    loadTemplateParams.template = null;

    // Set value
    loadTemplateParams[key] = value;
    if (key === 'period' && value !== filterParams.period) {
      filterParams.period = value
      updateReportStructureOptions()
    }
    getTemplateDataGroupOptions()
      .then(() => {
        getTemplateOptions();
      });
  }

  const openLoadReportTemplateDialog = () => {
    isOpenLoadReportTemplateDialog = true;
  }

  const closeLoadReportTemplateDialog = () => {
    isOpenLoadReportTemplateDialog = false;
  }

  const openSaveReportTemplateDialog = () => {
    isOpenSaveReportTemplateDialog = true;
  }

  const closeSaveReportTemplateDialog = () => {
    isOpenSaveReportTemplateDialog = false;
  }

  const initReportTemplateForm = () => {
    getTemplateDataGroupOptions()
      .then(() => getTemplateOptions())
      .then((result) => {
        if (!loadTemplateParams.template?.id || !templateOptions.find(t => t.id === loadTemplateParams.template?.id)) {
          // Un-selected unavailable template
          loadTemplateParams.template = null;
        }
      })
  }

  const getTemplateDataGroupOptions = (isSaveTemplate = false) => {
    if (!dataSourceCode || !loadTemplateParams.period || !loadTemplateParams.type) {
      dataGroupOptions = [];
      return Promise.resolve(dataGroupOptions);
    }

    /** HACK
     * If user is admin, retrieving data group with personal template type will return
     * list of usernames as Groups. However these are not actual groups and users should
     * not be allowed to save a template under those group.
     * 
     * Public template type will always return only the groups without users.
     */
    const templateType = isSaveTemplate ? TEMPLATE_TYPE.PUBLIC : loadTemplateParams.type;

    let dataGroupList = cachedTemplateDataGroup.get(`${loginModel.credentials.country}-${dataSourceCode}-${loadTemplateParams.period}-${templateType}`);

    if (dataGroupList) {
      dataGroupOptions = dataGroupList;
      return Promise.resolve(dataGroupOptions);
    }

    this.showLoading();

    return ReportModel.getReportConfigDataGroup(dataSourceCode, loadTemplateParams.period, templateType)
      .then((result) => {
        dataGroupOptions = result ?? [];

        cachedTemplateDataGroup.set(
          `${loginModel.credentials.country}-${dataSourceCode}-${loadTemplateParams.period}-${templateType}`,
          dataGroupOptions
        );

        return dataGroupOptions;
      })
      .catch((error) => {
        dataGroupOptions = [];
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const getTemplateOptions = () => {
    if (!dataSourceCode || !loadTemplateParams.period || !loadTemplateParams.type) {
      templateOptions = [];
      return Promise.resolve(templateOptions);
    }

    let templateList = cachedTemplates.get(`${loginModel.credentials.country}-${dataSourceCode}-${loadTemplateParams.period}-${loadTemplateParams.type}-${loadTemplateParams.group?.code ?? ''}`)

    if (templateList) {
      templateOptions = templateList;
      return Promise.resolve(templateOptions);
    }

    this.showLoading();

    return ReportModel.getReportTemplateList({
      dataSourceCode: dataSourceCode,
      periodCode: loadTemplateParams.period,
      templateType: loadTemplateParams.type,
      group: loadTemplateParams.group?.code ?? '',
    })
      .then(result => {
        templateOptions = result ?? [];
        cachedTemplates.set(
          `${loginModel.credentials.country}-${dataSourceCode}-${loadTemplateParams.period}-${loadTemplateParams.type}-${loadTemplateParams.group?.code ?? ''}`,
          templateOptions
        )

        return templateOptions;
      })
      .catch((error) => {
        this.handleError(error);
        templateOptions = [];
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const resetSaveTemplateForm = () => {
    saveTemplateParams = structuredClone(loadTemplateParams);
    m.redraw();
  }

  const populateTemplateDetails = (details) => {
    isLoadedFromTemplate = true;
    loadedTemplateDetails = details;
    filterParams.period = loadTemplateParams.period; // should get this from loadedTemplateDetails.period
    filterParams.fromDate = isMTDPeriod() ? moment(details.fromDate).format(FORMATS.API_MONTH_FORMAT) : details.fromDate;
    filterParams.toDate = isMTDPeriod() ? moment(details.toDate).format(FORMATS.API_MONTH_FORMAT) : details.toDate;
    if (details.fromCompareDate && details.toCompareDate) {
      filterParams.isCompareDates = true;
      filterParams.fromCompareDate = isMTDPeriod() ? moment(details.fromCompareDate).format(FORMATS.API_MONTH_FORMAT) : details.fromCompareDate;
      filterParams.toCompareDate = isMTDPeriod() ? moment(details.toCompareDate).format(FORMATS.API_MONTH_FORMAT) : details.toCompareDate;
      filterParams.mtdCompareDay = isMTDPeriod() ? moment(details.fromCompareDate).format("D") : '1';
    } else {
      filterParams.isCompareDates = false;
    }

    filterParams.mtdDay = isMTDPeriod() ? moment(details.fromDate).format('D') : '1';
    filterParams.parameters = details.filters;
    filterParams.xAxis = details.xAxis ? details.xAxis.split(KEYS_SEPARATOR).map(v => ({ code: v, name: v })) : [];
    filterParams.yAxis = details.yAxis ? details.yAxis.split(KEYS_SEPARATOR).map(v => ({ code: v, name: v })) : [];
    filterParams.zAxis = details.zAxis ? details.zAxis.split(KEYS_SEPARATOR).map(v => ({ code: v, name: v.split(KEY_VALUE_SEPARATOR).at(0) })) : [];
    filterParams.xAxisOrderedCodes = details.xAxis ? details.xAxis.split(KEYS_SEPARATOR) : [];
    filterParams.yAxisOrderedCodes = details.yAxis ? details.yAxis.split(KEYS_SEPARATOR) : [];
    filterParams.zAxisOrderedCodes = details.zAxis ? details.zAxis.split(KEYS_SEPARATOR) : [];
  }

  const loadTemplate = (e) => {
    // Form validation
    if (!this.validateForm(e, `#${SELECT_TEMPLATE_FORM_NAME}`)) {
      return false;
    }

    this.showLoading();
    ReportModel.getReportTemplate(loadTemplateParams.template.id)
      .then((data) => {
        populateTemplateDetails(data);
        if (getSettingByCode(INFO_SETTINGS.PERIOD_SELECTION_LIMIT)) {
          checkExceedTimeLimit(data.fromDate, data.toDate, "fromDate", "toDate");
          if (data.fromCompareDate && data.toCompareDate) {
            checkExceedTimeLimit(data.fromCompareDate, data.toCompareDate, "fromCompareDate", "toCompareDate");
          }
        }
      })
      .then(() => {
        closeLoadReportTemplateDialog();
      })
      .catch((error) => {
        this.handleError(error);
        loadedTemplateDetails = undefined;
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const handleDeleteButton = (e) => {
    e.preventDefault();

    this.showConfirm(
      this.getText('LT00000979', 'Do you want to delete the template?'),
      `${this.getText('LT00000135', 'Delete')} ${loadTemplateParams.template.name}`,
      (e) => {
        if (e === 'YES') {
          ReportModel.deleteReportTemplate(loadTemplateParams.template.id)
            .then(() => {
              // Unload template if currently loaded
              if (loadedTemplateDetails && loadTemplateParams && loadTemplateParams.template && loadedTemplateDetails.id === loadTemplateParams.template.id) {
                clearFilters();
              }
              // Remove cache & reset params
              if (loadTemplateParams) {
                cachedTemplates.delete(`${loginModel.credentials.country}-${dataSourceCode}-${loadTemplateParams.period}-${loadTemplateParams.type}-${loadTemplateParams.group?.code ?? ''}`);
                loadTemplateParams.template = null;
              }
              // Reload template list
              getTemplateOptions()
            })
            .catch((error) => {
              this.handleError(error)
            });
        }
      }
    )
  }

  const FilterWrapperComponent = () => {
    return {
      view: (vnode) => {
        return m('.col-12', vnode.attrs, vnode.children)
      }
    }
  }

  const initializeReportPage = () => {
    // Initialize data
    filterParams = structuredClone(DEFAULT_FILTER_PARAMS);
    lastGenerated = undefined;
    loadedTemplateDetails = undefined;
    loadTemplateParams = structuredClone(DEFAULT_TEMPLATE_PARAMS);
    saveTemplateParams = structuredClone(DEFAULT_TEMPLATE_PARAMS);

    return getPeriodTypes() // get periodTypes
      .then(() => updateReportStructureOptions()) // get report structure
  }

  const isRange = (periodType) => {
    if (!periodType) return false;

    return !!periodTypes.find(p => p.code.toLowerCase() === periodType.toLowerCase())?.isRange;
  }

  const renderDateInputLabel = () => {
    if (periodTypes?.length === 1) {
      if (periodTypes[0].code === DATE_TYPE_CODES.MONTH
        || periodTypes[0].code === DATE_TYPE_CODES.MTD) {
        return this.getText("LT00000326", "Month");
      }

      if (periodTypes[0].code === DATE_TYPE_CODES.WEEK) {
        return this.getText("LT00000337", "Week");
      }

      if (periodTypes[0].code === DATE_TYPE_CODES.DAY) {
        return this.getText("LT00000132", "Date");
      }

      if (periodTypes[0].code === DATE_TYPE_CODES.QUARTER) {
        return this.getText("LT00001129", "Quarter");
      }

      if (periodTypes[0].code === DATE_TYPE_CODES.YEAR) {
        return this.getText("LT00001128", "Year");
      }
    }

    return this.getText("LT00000968", "Range");
  }

  /**
   * Date Input Component for Day period type
   */
  const DateInputComponentForDay = () => {
    return {
      view: ({ attrs: { fromDateFilter, toDateFilter } }) => [
        m('.position-relative',
          m(DatePickerComponent, {
            id: `${fromDateFilter}_range`,
            class: 'form-control',
            isRange: isRange(filterParams.period),
            grid: 1,
            calendars: 1,
            date: isRange(filterParams.period)
              ? undefined
              : moment(filterParams[fromDateFilter]).format(FORMATS.dateFormat),
            dateStart: isRange(filterParams.period)
              ? filterParams[fromDateFilter]
                ? moment(filterParams[fromDateFilter]).format(FORMATS.dateFormat)
                : null
              : undefined,
            dateEnd: isRange(filterParams.period)
              ? filterParams[toDateFilter]
                ? moment(filterParams[toDateFilter]).format(FORMATS.dateFormat)
                : null
              : undefined,
            maxDate: moment().add(getMaxPeriod(), 'days').format(FORMATS.dateFormat),
            minDate: moment().add(getMinPeriod(), 'days').format(FORMATS.dateFormat),
            maxDays: getPeriodSelectionLimit(),
            readonly: true,
            required: true,
            onselect: (e) => {
              if (isRange(filterParams.period)) {
                setFilter(fromDateFilter, moment(e.detail.start).format(FORMATS.apiDateFormat));
                setFilter(toDateFilter, moment(e.detail.end).format(FORMATS.apiDateFormat));
                if (getSettingByCode(INFO_SETTINGS.PERIOD_SELECTION_LIMIT)) {
                  checkExceedTimeLimit(e.detail.start, e.detail.end, fromDateFilter, toDateFilter)
                }
              } else {
                setFilter(fromDateFilter, moment(e.detail.date).format(FORMATS.apiDateFormat));
                setFilter(toDateFilter, moment(e.detail.date).format(FORMATS.apiDateFormat));
              }
            }
          }),
          ((isCompareTimeLimitExceed && fromDateFilter === "fromCompareDate") || (isRangeTimeLimitExceed && fromDateFilter === "fromDate")) && m('.invalid-tooltip.bg-primary', { class: 'd-block' }, renderExceedTimeErrorMessage())
        )
      ]
    }
  }

  const checkExceedTimeLimit = (startDate, endDate, fromDateFilter, toDateFilter) => {
    if (!startDate) {
      startDate = DEFAULT_FILTER_PARAMS[fromDateFilter];
    }
    if (!endDate) {
      endDate = DEFAULT_FILTER_PARAMS[toDateFilter];
    }

    if (!startDate || !endDate || isNaN(getPeriodSelectionLimit())) {
      isRangeTimeLimitExceed = false;
      isCompareTimeLimitExceed = false;
      return
    }
    let { unit, format } = DIFF_MEASUREMENTS[filterParams.period]

    if (Math.abs(moment(startDate, format).diff(moment(endDate, format), unit)) > getPeriodSelectionLimit() - 1) {
      if (fromDateFilter === "fromCompareDate" && toDateFilter === "toCompareDate") {
        isCompareTimeLimitExceed = true;
      } else {
        isRangeTimeLimitExceed = true;
      }
    } else {
      if (fromDateFilter === "fromCompareDate" && toDateFilter === "toCompareDate") {
        isCompareTimeLimitExceed = false;
      }
      if (fromDateFilter === "fromDate" && toDateFilter === "toDate") {
        isRangeTimeLimitExceed = false;
      }
    }
  }

  const getDateTypeText = () => {
    switch (filterParams.period) {
      case DATE_TYPE_CODES.MONTH:
      case DATE_TYPE_CODES.MTD:
        return this.getText("LT00000326", "Month");

      case DATE_TYPE_CODES.WEEK:
        return this.getText("LT00000337", "Week");

      case DATE_TYPE_CODES.DAY:
        return this.getText("LT00001021", "Day");

      case DATE_TYPE_CODES.QUARTER:
        return this.getText("LT00001129", "Quarter");

      case DATE_TYPE_CODES.YEAR:
        return this.getText("LT00001128", "Year");

      default:
        return '';
    }
  }

  const renderExceedTimeErrorMessage = () => {
    const unit = getDateTypeText().toLowerCase();
    const limit = getPeriodSelectionLimit();
    return `${this.getText('EM00000820', 'Due to the large volume of data, generating or downloading report is limited to a maximum of')} ${limit} ${unit}${limit > 1 ? 's' : ''}.`
  }

  const TimeLimitTooltip = () => {
    return {
      view: () => {
        const limit = getPeriodSelectionLimit();
        if (!isNaN(limit) && limit > 0)
          return m(Tooltip, {
            content: renderExceedTimeErrorMessage(),
            hoverOpenDelay: 250,
            openOnTriggerFocus: true,
            position: PopoverPosition.RIGHT,
            trigger: m('i.bi.bi-info-circle.cursor-pointer.ps-1')
          })
      }
    }
  }
  /**
   * Date Input Component for Week period type
   */
  const DateInputComponentForWeek = ({ attrs: { fromDateFilter, toDateFilter } }) => {
    return {
      view: () => {
        return [
          m('.d-flex.flex-wrap.gap-1.position-relative',
            isRange(filterParams.period)
              ? [ // Week range
                m('.position-relative',
                  m('input#range.form-control[type="week"][required]', {
                    name: fromDateFilter,
                    min: moment().add(getMinPeriod(), 'weeks').format(FORMATS.WEEK_FORMAT),
                    max: moment().add(getMaxPeriod(), 'weeks').format(FORMATS.WEEK_FORMAT),
                    // max: filterParams.toDate
                    //   ? moment(filterParams.toDate, FORMATS.API_WEEK_FORMAT).format(FORMATS.WEEK_FORMAT)
                    //   : moment().add(getMaxPeriod(), 'weeks').format(FORMATS.WEEK_FORMAT),
                    value: filterParams[fromDateFilter]
                      ? moment(filterParams[fromDateFilter], FORMATS.API_WEEK_FORMAT).format(FORMATS.WEEK_FORMAT)
                      : null,
                    onchange: (e) => {
                      let selectedDate = e.target.value
                        ? moment(e.target.value, FORMATS.WEEK_FORMAT).format(FORMATS.API_WEEK_FORMAT)
                        : null;
                      if (moment(selectedDate, FORMATS.API_WEEK_FORMAT).isAfter(moment(filterParams[toDateFilter], FORMATS.API_WEEK_FORMAT))) {
                        filterParams[toDateFilter] = null;
                      }
                      if (getSettingByCode(INFO_SETTINGS.PERIOD_SELECTION_LIMIT)) {
                        checkExceedTimeLimit(selectedDate, filterParams[toDateFilter], fromDateFilter, toDateFilter);
                      }
                      setFilter(e.target.name, selectedDate);
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
                m('.px-2.my-auto', '~'),
                m('.position-relative',
                  m('input#range2.form-control[type="week"][required]', {
                    name: toDateFilter,
                    min: filterParams[fromDateFilter]
                      ? moment(filterParams[fromDateFilter], FORMATS.API_WEEK_FORMAT).format(FORMATS.WEEK_FORMAT)
                      : moment().add(getMinPeriod(), 'weeks').format(FORMATS.WEEK_FORMAT),
                    max: moment().add(getMaxPeriod(), 'weeks').format(FORMATS.WEEK_FORMAT),
                    value: filterParams[toDateFilter] ? moment(filterParams[toDateFilter], FORMATS.API_WEEK_FORMAT).format(FORMATS.WEEK_FORMAT) : null,
                    onchange: (e) => {
                      let selectedDate = e.target.value ? moment(e.target.value, FORMATS.WEEK_FORMAT).format(FORMATS.API_WEEK_FORMAT) : null;
                      if (getSettingByCode(INFO_SETTINGS.PERIOD_SELECTION_LIMIT)) {
                        checkExceedTimeLimit(filterParams[fromDateFilter], selectedDate, fromDateFilter, toDateFilter);
                      }
                      setFilter(e.target.name, selectedDate);
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
                ((isCompareTimeLimitExceed && fromDateFilter === "fromCompareDate") || (isRangeTimeLimitExceed && fromDateFilter === "fromDate")) && m('.invalid-tooltip.bg-primary', { class: 'd-block' }, renderExceedTimeErrorMessage())
              ]
              : [ // Single week
                m('.position-relative',
                  m('input#range.form-control[type="week"][required]', {
                    min: moment().add(getMinPeriod(), 'weeks').format(FORMATS.WEEK_FORMAT),
                    max: moment().add(getMaxPeriod(), 'weeks').format(FORMATS.WEEK_FORMAT),
                    value: filterParams[fromDateFilter] ? moment(filterParams[fromDateFilter], FORMATS.API_WEEK_FORMAT).format(FORMATS.WEEK_FORMAT) : null,
                    onchange: (e) => {
                      let selectedDate = e.target.value ? moment(e.target.value, FORMATS.WEEK_FORMAT).format(FORMATS.API_WEEK_FORMAT) : null;
                      setFilter(fromDateFilter, selectedDate);
                      setFilter(toDateFilter, selectedDate);
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
              ],
          ),
        ]
      }
    }
  }

  /**
   * Date Input Component for Month / MTD period type
   */
  const DateInputComponentForMonth = () => {
    return {
      view: ({ attrs: { fromDateFilter, toDateFilter } }) => {
        return [
          m('.d-flex.flex-wrap.gap-1.position-relative',
            isRange(filterParams.period)
              ? [ // Month range
                m('.position-relative',
                  m('input#range.form-control[type="month"][required]', {
                    name: fromDateFilter,
                    min: moment().add(getMinPeriod(), 'months').format(FORMATS.MONTH_FORMAT),
                    // max: filterParams.toDate
                    //   ? moment(filterParams.toDate, FORMATS.API_MONTH_FORMAT).format(FORMATS.MONTH_FORMAT)
                    //   : moment().add(getMaxPeriod(), 'months').format(FORMATS.MONTH_FORMAT),
                    max: moment().add(getMaxPeriod(), 'months').format(FORMATS.MONTH_FORMAT),
                    value: filterParams[fromDateFilter]
                      ? moment(filterParams[fromDateFilter], FORMATS.API_MONTH_FORMAT).format(FORMATS.MONTH_FORMAT)
                      : null,
                    onchange: (e) => {
                      let selectedDate = e.target.value
                        ? moment(e.target.value, FORMATS.MONTH_FORMAT).format(FORMATS.API_MONTH_FORMAT)
                        : null;
                      if (moment(selectedDate).isAfter(filterParams[toDateFilter])) {
                        filterParams[toDateFilter] = null;
                      }
                      if (getSettingByCode(INFO_SETTINGS.PERIOD_SELECTION_LIMIT)) {
                        checkExceedTimeLimit(selectedDate, filterParams[toDateFilter], fromDateFilter, toDateFilter);
                      }
                      setFilter(e.target.name, selectedDate);
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
                m('.px-2.my-auto', '~'),
                m('.position-relative',
                  m('input#range2.form-control[type="month"][required]', {
                    name: toDateFilter,
                    min: filterParams[fromDateFilter]
                      ? moment(filterParams[fromDateFilter], FORMATS.API_MONTH_FORMAT).format(FORMATS.MONTH_FORMAT)
                      : moment().add(getMinPeriod(), 'months').format(FORMATS.MONTH_FORMAT),
                    max: moment().add(getMaxPeriod(), 'months').format(FORMATS.MONTH_FORMAT),
                    value: filterParams[toDateFilter]
                      ? moment(filterParams[toDateFilter], FORMATS.API_MONTH_FORMAT).format(FORMATS.MONTH_FORMAT)
                      : null,
                    onchange: (e) => {
                      let selectedDate = e.target.value
                        ? moment(e.target.value, FORMATS.MONTH_FORMAT).format(FORMATS.API_MONTH_FORMAT)
                        : null;
                      if (getSettingByCode(INFO_SETTINGS.PERIOD_SELECTION_LIMIT)) {
                        checkExceedTimeLimit(filterParams[fromDateFilter], selectedDate, fromDateFilter, toDateFilter);
                      }
                      setFilter(e.target.name, selectedDate);
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
                ((isCompareTimeLimitExceed && fromDateFilter === "fromCompareDate") || (isRangeTimeLimitExceed && fromDateFilter === "fromDate")) && m('.invalid-tooltip.bg-primary', { class: 'd-block' }, renderExceedTimeErrorMessage())
              ]
              : [ // Single month
                m('.position-relative',
                  m('input#range.form-control[type="month"][required]', {
                    min: moment().add(getMinPeriod(), 'months').format(FORMATS.MONTH_FORMAT),
                    max: moment().add(getMaxPeriod(), 'months').format(FORMATS.MONTH_FORMAT),
                    value: filterParams[fromDateFilter] ? moment(filterParams[fromDateFilter], FORMATS.API_MONTH_FORMAT).format(FORMATS.MONTH_FORMAT) : null,
                    onchange: (e) => {
                      let selectedDate = e.target.value ? moment(e.target.value, FORMATS.MONTH_FORMAT).format(FORMATS.API_MONTH_FORMAT) : null;
                      setFilter(fromDateFilter, selectedDate);
                      setFilter(toDateFilter, selectedDate);
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
              ]
          ),
        ]
      }
    }
  }

  /**
   * Date Input Component for Quarter period type
   */
  const DateInputComponentForQuarter = () => {
    return {
      view: ({ attrs: { fromDateFilter, toDateFilter } }) => [
        m('.d-flex.gap-1',
          isRange(filterParams.period)
            ? [ // Quarter range
              m(QuarterPickerComponent, {
                id: 'range',
                max: moment().add(getMaxPeriod(), 'quarters').format(FORMATS.API_QUARTER_FORMAT),
                min: moment().add(getMinPeriod(), 'quarters').format(FORMATS.API_QUARTER_FORMAT),
                required: true,
                selectedItem: filterParams[fromDateFilter] ? filterParams[fromDateFilter] : null,
                onSelect: (item) => {
                  setFilter(fromDateFilter, item ? item : null);
                }
              }),
              m('.px-2.my-auto', '~'),
              m(QuarterPickerComponent, {
                id: 'range2',
                max: moment().add(getMaxPeriod(), 'quarters').format(FORMATS.API_QUARTER_FORMAT),
                min: moment().add(getMinPeriod(), 'quarters').format(FORMATS.API_QUARTER_FORMAT),
                required: true,
                selectedItem: filterParams[toDateFilter] ? filterParams[toDateFilter] : null,
                onSelect: (item) => {
                  setFilter(toDateFilter, item ? item : null);
                }
              }),
            ]
            : [ // Single quarter
              m(QuarterPickerComponent, {
                id: 'range',
                max: moment().add(getMaxPeriod(), 'quarters').format(FORMATS.API_QUARTER_FORMAT),
                min: moment().add(getMinPeriod(), 'quarters').format(FORMATS.API_QUARTER_FORMAT),
                required: true,
                selectedItem: filterParams[fromDateFilter] ? filterParams[fromDateFilter] : null,
                onSelect: (item) => {
                  setFilter(fromDateFilter, item ? item : null);
                  setFilter(toDateFilter, item ? item : null);
                }
              }),
            ],
        ),
      ]
    }
  }

  /**
   * Date Input Component for Year period type
   */
  const DateInputComponentForYear = () => {
    return {
      view: ({ attrs: { fromDateFilter, toDateFilter } }) => [
        m('.d-flex.gap-1',
          isRange(filterParams.period)
            ? [ // Year range
              m(YearPickerComponent, {
                id: 'range',
                max: moment().add(getMaxPeriod(), 'years').format(FORMATS.YEAR_FORMAT),
                min: moment().add(getMinPeriod(), 'years').format(FORMATS.YEAR_FORMAT),
                required: true,
                selectedItem: filterParams[fromDateFilter] ? filterParams[fromDateFilter] : null,
                onSelect: (item) => {
                  setFilter(fromDateFilter, item ? item : null);
                }
              }),
              m('.px-2.my-auto', '~'),
              m(YearPickerComponent, {
                id: 'range2',
                max: moment().add(getMaxPeriod(), 'years').format(FORMATS.YEAR_FORMAT),
                min: moment().add(getMinPeriod(), 'years').format(FORMATS.YEAR_FORMAT),
                required: true,
                selectedItem: filterParams[toDateFilter] ? filterParams[toDateFilter] : null,
                onSelect: (item) => {
                  setFilter(toDateFilter, item ? item : null);
                }
              }),
            ]
            : [ // Single year
              m(YearPickerComponent, {
                id: 'range',
                max: moment().add(getMaxPeriod(), 'years').format(FORMATS.YEAR_FORMAT),
                min: moment().add(getMinPeriod(), 'years').format(FORMATS.YEAR_FORMAT),
                required: true,
                selectedItem: filterParams[fromDateFilter] ? filterParams[fromDateFilter] : null,
                onSelect: (item) => {
                  setFilter(fromDateFilter, item ? item : null);
                  setFilter(toDateFilter, item ? item : null);
                }
              }),
            ],
        ),
      ]
    }
  }

  /**
   * Report Period Section
   */
  const SectionReportPeriod = () => {
    return {
      view: () => [
        m('h4.my-2', this.getText("LT00000967", "Report Period")),
        m('.card',
          m('.card-body',
            m('.d-flex.gap-4.flex-wrap',
              periodTypes?.length > 1 &&
              m('.d-flex.flex-column.flex-shrink-0',
                m('label.form-label[for=period]', this.getText('LT00000150', 'Period')),
                m(ToggleButtonGroupComponent, {
                  ariaLabel: this.getText('LT00000150', 'Period'),
                  buttonType: BUTTON_TYPE.RADIO,
                  buttons: periodTypes.map(p => ({
                    checked: filterParams.period === p.code,
                    id: p.code,
                    label: p.name,
                    value: p.code,
                  })),
                  inline: true,
                  name: 'period',
                  onChange: (e) => {
                    setFilter(e.target.name, e.target.value);
                    clearFilters();
                    updateReportStructureOptions();
                  },
                }),
              ),
              m('.d-flex.flex-column',
                m('label.form-label.mandatory[for=range]',
                  renderDateInputLabel(),
                  m(TimeLimitTooltip)
                ),
                isDayPeriod() &&
                m(DateInputComponentForDay, { fromDateFilter: 'fromDate', toDateFilter: 'toDate' }),
                isWeekPeriod() &&
                m(DateInputComponentForWeek, { fromDateFilter: 'fromDate', toDateFilter: 'toDate' }),
                (isMonthPeriod() || isMTDPeriod()) &&
                m(DateInputComponentForMonth, { fromDateFilter: 'fromDate', toDateFilter: 'toDate' }),
                isYearPeriod() &&
                m(DateInputComponentForYear, { fromDateFilter: 'fromDate', toDateFilter: 'toDate' }),
                isQuarterPeriod() &&
                m(DateInputComponentForQuarter, { fromDateFilter: 'fromDate', toDateFilter: 'toDate' }),
              ),
              isMTDPeriod() &&
              m('.d-flex.flex-column.flex-shrink-0',
                m('label.form-label[for=day]', this.getText('LT00001021', 'Day')),
                m(SingleSelectComponent, {
                  id: 'day',
                  keyOnly: true,
                  items: DAY_OPTIONS,
                  selectedItem: filterParams.mtdDay,
                  onSelect: (selected) => {
                    filterParams.mtdDay = selected;
                  }
                }),
              ),
              getSettingByCode(INFO_SETTINGS.COMPARE_DATE) && m('.d-flex.flex-column',
                m('div.form-label',
                  m(Checkbox, {
                    class: 'align-middle',
                    label: this.getText('LT00001186', 'Compare Date'),
                    checked: filterParams.isCompareDates,
                    onchange: (e) => {
                      setFilter('isCompareDates', e.target.checked);
                      // setCompareDateFilter('isCompareDates', e.target.checked);
                    }
                  })
                ),
                filterParams.isCompareDates && [
                  isDayPeriod() &&
                  m(DateInputComponentForDay, { fromDateFilter: 'fromCompareDate', toDateFilter: 'toCompareDate' }),
                  isWeekPeriod() &&
                  m(DateInputComponentForWeek, { fromDateFilter: 'fromCompareDate', toDateFilter: 'toCompareDate' }),
                  (isMonthPeriod() || isMTDPeriod()) &&
                  m(DateInputComponentForMonth, { fromDateFilter: 'fromCompareDate', toDateFilter: 'toCompareDate' }),
                  isYearPeriod() &&
                  m(DateInputComponentForYear, { fromDateFilter: 'fromCompareDate', toDateFilter: 'toCompareDate' }),
                  isQuarterPeriod() &&
                  m(DateInputComponentForQuarter, { fromDateFilter: 'fromCompareDate', toDateFilter: 'toCompareDate' }),
                ],
              ),
              isMTDPeriod() && filterParams.isCompareDates &&
              m('.d-flex.flex-column.flex-shrink-0',
                m('label.form-label[for=day]', this.getText('LT00001021', 'Day')),
                m(SingleSelectComponent, {
                  id: 'day',
                  keyOnly: true,
                  items: DAY_OPTIONS,
                  selectedItem: filterParams.mtdCompareDay,
                  onSelect: (selected) => {
                    filterParams.mtdCompareDay = selected;
                  }
                })
              )
            )
          )
        )
      ]
    }
  }

  /**
   * Report Filter Section
   */
  const SectionReportFilter = () => {
    return {
      view: () => [
        m('h4.my-2', this.getText("LT00000969", "Report Filter")),
        m('.card',
          m('.card-body',
            m('.row.g-4',
              m(DynamicReportFilterComponent, {
                data: {
                  dataSourceCode: dataSourceCode,
                  periodCode: filterParams.period,
                  parameters: filterParams.parameters,
                  isLoadedFromTemplate: isLoadedFromTemplate,
                },
                buttonWrapperComponent: FilterWrapperComponent,
                filterWrapperComponent: FilterWrapperComponent,
                onChange: (selectedFilters, _isLoadedFromTemplate = false) => {
                  filterParams.parameters = selectedFilters;
                  isLoadedFromTemplate = _isLoadedFromTemplate;
                },
              }),
            ),
          ),
        ),
      ]
    }
  }

  /**
   * Report Structure Section
   */
  const SectionReportStructure = () => {
    return {
      view: () => [
        m('h4.my-2', this.getText("LT00000970", "Report Structure")),
        m('.card',
          m('.card-body',
            m('.row.g-4',
              m('.col-12',
                m('label.form-label[for=columnHeaders]', `${this.getText("LT00000971", "Column Headers")} (${this.getText("LT00000976", "X-Axis")})`),
                m(SortableMultiSelectComponent, {
                  id: 'columnHeaders',
                  items: xAxisOptions,
                  selectedItems: filterParams.xAxis,
                  onChange: (updatedItems, updatedOrder) => {
                    filterParams.xAxis = updatedItems;
                    filterParams.xAxisOrderedCodes = updatedOrder;
                  },
                }),
              ),
              m('.col-12.position-relative',
                m('label.form-label.mandatory[for=rowHeaders]', `${this.getText("LT00000972", "Row Headers")} (${this.getText("LT00000977", "Y-Axis")})`),
                m(SortableMultiSelectComponent, {
                  id: 'rowHeaders',
                  items: yAxisOptions,
                  selectedItems: filterParams.yAxis,
                  required: true,
                  onChange: (updatedItems, updatedOrder) => {
                    filterParams.yAxis = updatedItems;
                    filterParams.yAxisOrderedCodes = updatedOrder;
                  },
                }),
                m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
              ),
              m('.col-12.position-relative',
                m('label.form-label.mandatory[for=dataDisplay]', `${this.getText("LT00000973", "Data Display")} (${this.getText("LT00000978", "Z-Axis")})`),
                m(SortableMultiSelectComponent, {
                  id: 'dataDisplay',
                  items: zAxisOptions,
                  selectedItems: filterParams.zAxis,
                  sortableListAttrs: {
                    listItemAttrs: {
                      showKeyInTitle: true,
                    }
                  },
                  required: true,
                  onChange: (updatedItems, updatedOrder) => {
                    filterParams.zAxis = updatedItems;
                    filterParams.zAxisOrderedCodes = updatedOrder;
                  },
                }),
                m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
              ),
            ),
          ),
        ),
      ]
    }
  }

  /**
   * Load Report Template Dialog
   */
  const DialogLoadReportTemplate = () => {
    return {
      view: () => [
        m(Dialog, {
          title: this.getText('LT00000964', 'Report Template'),
          isOpen: isOpenLoadReportTemplateDialog,
          onClose: () => { closeLoadReportTemplateDialog() },
          onClosed: () => { resetLoadTemplateParams() },
          onOpened: () => { initReportTemplateForm() },
          closeOnOutsideClick: false,
          content: [
            m('.container.pt-2.pb-4',
              m('form.row.gy-2.align-items-center.needs-validation[novalidate]', {
                id: SELECT_TEMPLATE_FORM_NAME,
                onsubmit: loadTemplate,
              },
                periodTypes?.length > 1 &&
                m('.col-2',
                  m('label.form-label.m-0', this.getText('LT00000150', 'Period')),
                ),
                periodTypes?.length > 1 &&
                m('.col-10',
                  m(ToggleButtonGroupComponent, {
                    ariaLabel: this.getText('LT00000150', 'Period'),
                    buttonType: BUTTON_TYPE.RADIO,
                    buttons: periodTypes.map(p => ({
                      checked: loadTemplateParams.period === p.code,
                      id: `template-period-${p.code}`,
                      label: p.name,
                      value: p.code,
                    })),
                    name: 'period',
                    onChange: (e) => {
                      handleTemplateFieldChange(e.target.name, e.target.value);
                    },
                  }),
                ),
                m('.col-2',
                  m('label.form-label.m-0', this.getText('LT00000117', 'Type')),
                ),
                m('.col-10',
                  m(ToggleButtonGroupComponent, {
                    ariaLabel: this.getText('LT00000117', 'Type'),
                    buttonType: BUTTON_TYPE.RADIO,
                    buttons: Object.values(TEMPLATE_TYPE).map(type => ({
                      checked: loadTemplateParams.type === type,
                      id: type,
                      label: type,
                      value: type,
                    })),
                    name: 'type',
                    onChange: (e) => {
                      handleTemplateFieldChange(e.target.name, e.target.value);
                    },
                  }),
                ),
                m('.col-2',
                  m('label.form-label.m-0', this.getText('LT00000974', 'Group')),
                ),
                m('.col-10',
                  m(SingleSelectComponent, {
                    items: dataGroupOptions,
                    selectedItem: loadTemplateParams.group,
                    onSelect: (selected) => {
                      handleTemplateFieldChange('group', selected);
                    }
                  }),
                ),
                m('.col-2',
                  m('label.form-label.mandatory.m-0', this.getText('LT00000202', 'Template')),
                ),
                m('.col-10.position-relative',
                  m(SingleSelectComponent, {
                    itemKey: 'id',
                    itemValue: 'name',
                    items: templateOptions,
                    selectedItem: loadTemplateParams.template,
                    required: true,
                    onSelect: (selected) => {
                      loadTemplateParams.template = selected;
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
              )
            ),
            this.getBaseViews()
          ], // content - end
          footer: [
            m('.d-flex.justify-content-end.gap-2',
              m('button.btn.btn-primary[type="submit"]', {
                form: SELECT_TEMPLATE_FORM_NAME
              },
                this.getText('LT00000975', 'Load')
              ),
              m('button.btn.btn-danger', {
                disabled: !loadTemplateParams.template?.canDelete,
                onclick: handleDeleteButton,
              }, this.getText('LT00000135', 'Delete')),
              m('button.btn.btn-white', {
                onclick: () => { closeLoadReportTemplateDialog() },
              }, this.getText('LT00000021', 'Close')),
            ),
            this.getBaseViews()
          ], // footer - end
        }),
      ]
    }
  }

  /**
   * Save Report Template Dialog
   */
  const DialogSaveReportTemplate = () => {
    return {
      view: () => [
        m(Dialog, {
          title: this.getText('LT00000964', 'Report Template'),
          isOpen: isOpenSaveReportTemplateDialog,
          onClose: () => { closeSaveReportTemplateDialog() },
          onOpened: () => {
            getTemplateDataGroupOptions(true);
            resetSaveTemplateForm();
          },
          closeOnOutsideClick: false,
          content: [
            m('.container.pt-2.pb-4',
              m('form.row.g-2.align-items-center.needs-validation[novalidate]', {
                id: SAVE_TEMPLATE_FORM_NAME,
                onsubmit: submitSaveTemplateForm,
              },
                m('.col-12.col-sm-3',
                  m('label.form-label.mandatory.m-0', this.getText('LT00000300', 'Template Name')),
                ),
                m('.col-12.col-sm-9.position-relative',
                  m('input.form-control', {
                    id: 'templateName',
                    // make sure the value is valid, otherwise flexmoster will throw a TypeError: Cannot read properties of undefined (reading 'toLowerCase')
                    value: saveTemplateParams?.template?.name || '',
                    placeholder: this.getText('LT00000300', 'Template Name'),
                    required: true,
                    onchange: (e) => {
                      if (!saveTemplateParams.template) saveTemplateParams.template = {};
                      saveTemplateParams.template.name = e.target.value;
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
                m('.col-12.col-sm-3.mt-3.mt-sm-2',
                  m('label.form-label.m-0', this.getText('LT00000974', 'Group')),
                ),
                m('.col-12.col-sm-9',
                  m(SingleSelectComponent, {
                    items: dataGroupOptions,
                    selectedItem: saveTemplateParams.group,
                    onSelect: (selected) => {
                      saveTemplateParams.group = selected;
                    }
                  }),
                ),
                m('.col-12.col-sm-3.mt-3.mt-sm-2',
                  m('label.form-label.m-0', this.getText('LT00000117', 'Type')),
                ),
                m('.col-12.col-sm-9',
                  m(ToggleButtonGroupComponent, {
                    ariaLabel: this.getText('LT00000117', 'Type'),
                    buttonType: BUTTON_TYPE.RADIO,
                    buttons: Object.values(TEMPLATE_TYPE).map(type => ({
                      checked: saveTemplateParams.type === type,
                      id: type,
                      label: type,
                      value: type,
                    })),
                    name: 'type',
                    onChange: (e) => {
                      saveTemplateParams.type = e.target.value;
                    },
                  }),
                ),
              )
            ),
            this.getBaseViews()
          ], // content - end
          footer: [
            m('.d-flex.justify-content-end.gap-2',
              m('button.btn.btn-primary[type="submit"]', {
                form: SAVE_TEMPLATE_FORM_NAME
              },
                this.getText('LT00000022', 'Save')
              ),
              m('button.btn.btn-white', {
                onclick: () => { closeSaveReportTemplateDialog() },
              }, this.getText('LT00000021', 'Close')),
            ),
            this.getBaseViews()
          ], // footer - end
        }),
      ]
    }
  }

  return {
    oninit: () => {
      getDataSourceCode();
      initializeReportPage();
    },
    oncreate: () => {
      reportFilterSection = document.getElementById("report-filter-section");
    },
    onupdate: () => {
      // Re-initialize when nagivating to other report page
      isMsa = m.route.param().msa !== undefined;
      if (getDataSourceCode()) {
        initializeReportPage();
        clearFilters();
        m.redraw();
      }
    },
    view: () => {
      return [
        // Filter section
        m('.d-flex.flex-column.align-items-end.gap-2.mb-3',
          m('.d-flex.align-items-center.justify-content-between.gap-2.w-100',
            m('a.btn.btn-primary.flex-shrink-0[role="button"]', {
              onclick: () => { openLoadReportTemplateDialog() },
              title: this.getText('LT00000966', 'Load template'),
            },
              m('i.bi.bi-journal-bookmark.me-0.me-sm-2'),
              m('span.d-none.d-sm-inline.ms-1', this.getText('LT00000966', 'Load template')),
            ),
            loadedTemplateDetails?.name &&
            m('.flex-grow-1.text-start.overflow-hidden',
              m('label.d-block.fw-bold.text-truncate', { title: loadedTemplateDetails?.name ?? '' }, loadedTemplateDetails?.name ?? ''),
            ),
            m('a.btn.btn-primary.flex-shrink-0[role="button"][data-bs-toggle="collapse"][data-bs-target="#report-filter-section"][aria-controls="report-filter-section"][aria-expanded="true"]',
              m('i.bi.bi-filter'),
              this.getText("LT00000232", "Filter"),
            ),
          ),
          m('#report-filter-section.collapse.show.w-100',
            m('.card.shadow-border',
              m('.card-body.px-4',
                m('form#report-generate-form.needs-validation[novalidate]', {
                  onsubmit: generateReport
                },
                  m('.row.g-4',
                    m('.col-12',
                      m(SectionReportPeriod),
                    ),
                    m('.col-12.col-md-6',
                      m(SectionReportFilter),
                    ),
                    m('.col-12.col-md-6',
                      m(SectionReportStructure),
                    ),
                  ),

                  m('.row.g-3.my-3',
                    // Generate & clear buttons
                    m('.col-12.d-flex.justify-content-end.gap-2',
                      m('button.btn.btn-primary[type=submit]', {
                        disabled: isRangeTimeLimitExceed || isCompareTimeLimitExceed
                      },
                        this.getText("LT00001003", "Generate")
                      ),
                      m('button.btn.btn-white', {
                        onclick: clearFilters
                      }, this.getText("LT00000031", "Clear")),
                      m('button.btn.btn-white', {
                        onclick: saveTemplate
                      }, this.getText("LT00000022", "Save")),
                      m('button.btn.btn-primary', {
                        disabled: isRangeTimeLimitExceed || isCompareTimeLimitExceed,
                        onclick: downloadReport
                      }, this.getText("LT00001166", "Download Raw Data")),
                    ),
                  ),
                ),
              )
            )
          ),
        ),

        // Pivot table component
        m(PivotTableComponent, {
          id: 'customized-report-pivot',
          lastGenerated: lastGenerated,
          reportParams: _getParams,
          showFilter: showFilterAndScrollTop,
          version: isMsa ? 'V2' : '',
        }),

        // Dialog to Load Report Template
        m(DialogLoadReportTemplate),

        // Dialog to Save Report Template
        m(DialogSaveReportTemplate),
        this.getBaseViews()
      ]
    }
  }
}
